import { memo, useCallback } from 'react';
import {
  Control,
  Controller,
  UseFormSetValue,
  useWatch,
} from 'react-hook-form';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import type { RulesEngineFormValues } from './rules-engine-form-page';
import { GROUP_FIELD_INFO_TYPES, OPERATORS } from './condition-block-constants';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

interface GroupConditionBlockProps {
  blockIndex: number;
  control: Control<RulesEngineFormValues>;
  setValue: UseFormSetValue<RulesEngineFormValues>;
  conditions: any[];
  conditionBlocks: any[];
  triggerGroup: string;
}

interface GroupConditionRowProps {
  blockIndex: number;
  conditionIndex: number;
  control: Control<RulesEngineFormValues>;
  setValue: UseFormSetValue<RulesEngineFormValues>;
  conditions: any[];
  triggerGroup: string;
  onRemove: (index: number) => void;
  onLogicalOperatorChange: (index: number, operator: 'AND' | 'OR') => void;
}

const GroupConditionRow = memo(function GroupConditionRow({
  blockIndex,
  conditionIndex,
  control,
  setValue,
  conditions,
  triggerGroup,
  onRemove,
  onLogicalOperatorChange,
}: GroupConditionRowProps) {
  const infoType =
    useWatch({
      control,
      name: `conditionBlocks.${blockIndex}.conditions.${conditionIndex}.fieldInfoType`,
    }) || 'groupName';

  const operatorValue =
    useWatch({
      control,
      name: `conditionBlocks.${blockIndex}.conditions.${conditionIndex}.operator`,
    }) || 'contains';

  const isGroupName = infoType === 'groupName';
  const isMemberCount = infoType === 'memberCount';

  return (
    <Box>
      {conditionIndex > 0 && (
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            mb: 3,
            alignItems: 'center',
          }}
        >
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Button
              variant={
                conditions[conditionIndex - 1]?.logicalOperator !== 'OR'
                  ? 'contained'
                  : 'outlined'
              }
              size="small"
              sx={{ minWidth: '50px', textTransform: 'none' }}
              onClick={() => onLogicalOperatorChange(conditionIndex, 'AND')}
            >
              And
            </Button>
            <Button
              variant={
                conditions[conditionIndex - 1]?.logicalOperator === 'OR'
                  ? 'contained'
                  : 'outlined'
              }
              size="small"
              sx={{ minWidth: '50px', textTransform: 'none' }}
              onClick={() => onLogicalOperatorChange(conditionIndex, 'OR')}
            >
              Or
            </Button>
          </Box>
        </Box>
      )}

      <Grid
        container
        spacing={1}
        alignItems="center"
        sx={{
          pl: 2,
          borderLeft: '2px solid',
          borderLeftColor: 'primary.main',
        }}
      >
        {/* Field Info Type Select */}
        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: operatorValue === 'isEmpty' && isMemberCount ? 5 : 3,
          }}
        >
          <Controller
            name={`conditionBlocks.${blockIndex}.conditions.${conditionIndex}.fieldInfoType`}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                select
                fullWidth
                size="small"
                label={
                  triggerGroup === 'groupEvents'
                    ? 'Which field?'
                    : 'What kind of information?'
                }
                value={field.value || ''}
                onChange={(e) => {
                  const val = e.target.value;
                  field.onChange(val);

                  setValue(
                    `conditionBlocks.${blockIndex}.conditions.${conditionIndex}.fieldName`,
                    val
                  );

                  // Reset value when type changes
                  setValue(
                    `conditionBlocks.${blockIndex}.conditions.${conditionIndex}.value`,
                    ''
                  );

                  // Set default operator based on the new field type
                  const defaultOperator =
                    val === 'memberCount' ? 'equals' : 'contains';

                  setValue(
                    `conditionBlocks.${blockIndex}.conditions.${conditionIndex}.operator`,
                    defaultOperator
                  );

                  // Reset secondary value for memberCount
                  if (val === 'memberCount') {
                    setValue(
                      `conditionBlocks.${blockIndex}.conditions.${conditionIndex}.secondaryValue`,
                      ''
                    );
                  }
                }}
              >
                {GROUP_FIELD_INFO_TYPES.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </Grid>

        {/* Operator Select */}
        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: operatorValue === 'isEmpty' && isMemberCount ? 6 : 3,
          }}
        >
          <Controller
            name={`conditionBlocks.${blockIndex}.conditions.${conditionIndex}.operator`}
            control={control}
            render={({ field }) => {
              const operatorOptions = isMemberCount
                ? [
                    { value: 'equals', label: 'equals' },
                    { value: 'greaterThan', label: 'greater than' },
                    { value: 'lessThan', label: 'less than' },
                    {
                      value: 'greaterThanOrEqual',
                      label: 'greater than or equal to',
                    },
                    {
                      value: 'lessThanOrEqual',
                      label: 'less than or equal to',
                    },
                    { label: 'is empty', value: 'isEmpty' },
                    { label: 'is between', value: 'between' },
                    { label: 'is not between', value: 'isNotBetween' },
                  ]
                : OPERATORS;

              const defaultOperator = isMemberCount
                ? 'greaterThanOrEqual'
                : 'contains';

              const currentOperator = field.value || defaultOperator;

              return (
                <TextField
                  {...field}
                  select
                  fullWidth
                  size="small"
                  label="Operator"
                  value={currentOperator}
                  onChange={(e) => {
                    field.onChange(e.target.value || defaultOperator);
                  }}
                >
                  {operatorOptions.map((op) => (
                    <MenuItem key={op.value} value={op.value}>
                      {op.label}
                    </MenuItem>
                  ))}
                </TextField>
              );
            }}
          />
        </Grid>

        {/* Value Input Column */}
        {!(operatorValue === 'isEmpty' && isMemberCount) && (
          <Grid
            size={{
              xs: 12,
              sm: 6,
              md: 5,
            }}
          >
            {/* 1. Group Name Input Renderer */}
            {isGroupName && (
              <Controller
                name={`conditionBlocks.${blockIndex}.conditions.${conditionIndex}.value`}
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    fullWidth
                    size="small"
                    label="Group Name"
                    value={field.value ?? ''}
                    onChange={(e) => field.onChange(e.target.value)}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            )}

            {/* 2. Member Count Input Renderer */}
            {isMemberCount &&
              (() => {
                const showSecondValue =
                  operatorValue === 'between' ||
                  operatorValue === 'isNotBetween';
                const isEmptyOperator = operatorValue === 'isEmpty';

                if (isEmptyOperator) {
                  return null;
                }

                const firstLabel =
                  operatorValue === 'between'
                    ? 'at least'
                    : operatorValue === 'isNotBetween'
                    ? 'fewer than'
                    : 'Count';
                const separator =
                  operatorValue === 'isNotBetween' ? 'or' : 'and';
                const secondLabel =
                  operatorValue === 'between'
                    ? 'at most'
                    : operatorValue === 'isNotBetween'
                    ? 'more than'
                    : 'Count';

                return (
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    flexWrap="wrap"
                    sx={{ width: '100%' }}
                  >
                    <Controller
                      name={`conditionBlocks.${blockIndex}.conditions.${conditionIndex}.value`}
                      control={control}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          fullWidth
                          type="number"
                          size="small"
                          placeholder={firstLabel}
                          value={field.value ?? ''}
                          onKeyDown={(e) => {
                            if (e.key === '-') {
                              e.preventDefault();
                            }
                          }}
                          inputProps={{ min: 0 }}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (val && parseFloat(val) < 0) {
                              field.onChange('');
                            } else {
                              field.onChange(val);
                            }
                          }}
                          sx={{ flex: 1, minWidth: 120 }}
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message}
                        />
                      )}
                    />
                    {showSecondValue && (
                      <>
                        <Typography sx={{ px: 0.5 }}>{separator}</Typography>
                        <Controller
                          name={`conditionBlocks.${blockIndex}.conditions.${conditionIndex}.secondaryValue`}
                          control={control}
                          render={({ field, fieldState }) => (
                            <Typography
                              component="div"
                              sx={{ flex: 1, minWidth: 120 }}
                            >
                              <TextField
                                {...field}
                                fullWidth
                                type="number"
                                size="small"
                                placeholder={secondLabel}
                                value={field.value ?? ''}
                                onKeyDown={(e) => {
                                  if (e.key === '-') {
                                    e.preventDefault();
                                  }
                                }}
                                inputProps={{ min: 0 }}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  if (val && parseFloat(val) < 0) {
                                    field.onChange('');
                                  } else {
                                    field.onChange(val);
                                  }
                                }}
                                error={!!fieldState.error}
                                helperText={fieldState.error?.message}
                              />
                            </Typography>
                          )}
                        />
                      </>
                    )}
                  </Stack>
                );
              })()}
          </Grid>
        )}

        {/* Delete Condition Button */}
        <Grid
          size={{ xs: 12, sm: 12, md: 1 }}
          sx={{ display: 'flex', alignItems: 'center', minHeight: 40 }}
        >
          <Tooltip title="Delete condition">
            <IconButton size="small" onClick={() => onRemove(conditionIndex)}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Grid>
      </Grid>
    </Box>
  );
});

const GroupConditionBlock = memo(function GroupConditionBlock({
  blockIndex,
  control,
  setValue,
  conditions,
  conditionBlocks,
  triggerGroup,
}: GroupConditionBlockProps) {
  const handleLogicalOperatorChange = useCallback(
    (conditionIndex: number, operator: 'AND' | 'OR') => {
      const updatedConditions = [...conditions];
      updatedConditions[conditionIndex - 1] = {
        ...updatedConditions[conditionIndex - 1],
        logicalOperator: operator,
      };
      const updatedBlock = {
        ...conditionBlocks[blockIndex],
        conditions: updatedConditions,
      };
      const updatedBlocks = [...conditionBlocks];
      updatedBlocks[blockIndex] = updatedBlock;
      setValue('conditionBlocks', updatedBlocks);
    },
    [conditions, conditionBlocks, blockIndex, setValue]
  );

  const ensureValidOperator = useCallback((condition: any): any => {
    if (!condition.operator) {
      const fieldInfoType = condition.fieldInfoType;
      const defaultOperator =
        fieldInfoType === 'memberCount' ? 'greaterThanOrEqual' : 'contains';
      return { ...condition, operator: defaultOperator };
    }
    return condition;
  }, []);

  const handleRemove = useCallback(
    (conditionIndex: number) => {
      const updatedConditions = conditions
        .filter((_: any, i: number) => i !== conditionIndex)
        .map((cond: any) => ensureValidOperator(cond));
      if (updatedConditions.length === 0) {
        setValue(
          'conditionBlocks',
          conditionBlocks.filter(
            (_: any, index: number) => index !== blockIndex
          )
        );
        return;
      }
      const updatedBlock = {
        ...conditionBlocks[blockIndex],
        conditions: updatedConditions,
      };
      const updatedBlocks = [...conditionBlocks];
      updatedBlocks[blockIndex] = updatedBlock;
      setValue('conditionBlocks', updatedBlocks);
    },
    [conditions, conditionBlocks, blockIndex, setValue, ensureValidOperator]
  );

  return (
    <Stack gap={2}>
      {conditions.map((condition, conditionIndex) => (
        <GroupConditionRow
          key={condition.id}
          blockIndex={blockIndex}
          conditionIndex={conditionIndex}
          control={control}
          setValue={setValue}
          conditions={conditions}
          triggerGroup={triggerGroup}
          onRemove={handleRemove}
          onLogicalOperatorChange={handleLogicalOperatorChange}
        />
      ))}
    </Stack>
  );
});

export default GroupConditionBlock;
